/**
 * Configuration Processing Service - Pure Functions
 *
 * Contains pure functions for configuration processing with NO side effects:
 * - No store access
 * - No toasts
 * - No API calls
 *
 * All functions take inputs and return outputs without modifying external state.
 */

import { buildOptimizedCandidates } from '~/builders/optimized-candidate-builder';
import { findMatchingConfiguration } from '~/services/configuration-comparison';
import type { BondSeries, Configuration, Project } from '~/types';

// ============================================
// Types
// ============================================

/**
 * Result of a configuration processing operation
 */
export interface ProcessingResult {
  success: boolean;
  configuration: Configuration;
  candidateCount: number;
  processingTimeMs: number;
  error?: string;
}

/**
 * Result of checking for matching configuration
 */
export interface MatchingConfigResult {
  found: boolean;
  configuration?: Configuration;
}

// ============================================
// Pure Functions
// ============================================

/**
 * Process a configuration to generate candidates
 * Pure function - no side effects, returns processed configuration
 *
 * @param project - The project context
 * @param configuration - The configuration to process
 * @returns ProcessingResult with processed configuration and candidates
 */
export function processConfigurationCandidates(
  project: Project,
  configuration: Configuration
): ProcessingResult {
  const startTime = Date.now();

  try {
    const { candidates, failedCandidates } = buildOptimizedCandidates(project, configuration);
    const processingTimeMs = Date.now() - startTime;

    const processedConfiguration: Configuration = {
      ...configuration,
      candidates,
      failedCandidates,
      totalCandidates: candidates.length,
    };

    return {
      success: true,
      configuration: processedConfiguration,
      candidateCount: candidates.length,
      processingTimeMs,
    };
  } catch (error) {
    const processingTimeMs = Date.now() - startTime;
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    return {
      success: false,
      configuration,
      candidateCount: 0,
      processingTimeMs,
      error: errorMessage,
    };
  }
}

/**
 * Check if there's an existing configuration that matches the given parameters
 * Pure function - only performs comparison, no side effects
 *
 * @param configurations - Array of existing configurations to check
 * @param bondSeries - The bond series to match
 * @param millageInputs - The millage inputs to match
 * @param drfInputs - The DRF inputs to match
 * @returns MatchingConfigResult indicating if a match was found
 */
export function checkForMatchingConfiguration(
  configurations: Configuration[],
  bondSeries: BondSeries[],
  millageInputs: Configuration['millageInputs'],
  drfInputs: Configuration['drfInputs']
): MatchingConfigResult {
  const existingConfig = findMatchingConfiguration(
    configurations,
    bondSeries,
    millageInputs,
    drfInputs
  );

  if (existingConfig) {
    return {
      found: true,
      configuration: existingConfig,
    };
  }

  return { found: false };
}

/**
 * Determine if an error is a "no viable candidates" error
 * Useful for showing appropriate user messaging
 */
export function isNoViableCandidatesError(error: string | undefined): boolean {
  if (!error) return false;
  return error.includes('Unable to generate viable bond structures');
}
